import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Tag } from "lucide-react";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { Input } from "@/components/ui/input";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function LeadSources() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const auth = useMemo(() => {
    const token = localStorage.getItem("session_token");
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/lead-sources`, auth);
      setSources(data);
    } catch (err) {
      toast.error("Failed to load lead sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, auth);
        setUser(me.data);
        if (me.data.role !== "Admin") { navigate("/jobs"); return; }
      } catch { navigate("/login"); return; }
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/lead-sources`, { label }, auth);
      setNewLabel("");
      toast.success("Added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (src, newLabel) => {
    const label = newLabel.trim();
    if (!label || label === src.label) return;
    try {
      await axios.put(`${API}/lead-sources/${src.source_id}`, { label }, auth);
      toast.success("Renamed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed");
    }
  };

  const handleDelete = async (src) => {
    if (!window.confirm(`Remove "${src.label}"?`)) return;
    try {
      await axios.delete(`${API}/lead-sources/${src.source_id}`, auth);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleToggle = async (src) => {
    try {
      await axios.put(`${API}/lead-sources/${src.source_id}`, { enabled: !src.enabled }, auth);
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <Header user={user} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/settings")} className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-3 text-sm"><ArrowLeft className="w-4 h-4" />Back to Settings</button>
        <div className="mb-5">
          <h1 className="font-barlow text-2xl font-bold text-zinc-950 uppercase flex items-center gap-2"><Tag className="w-6 h-6 text-[#207024]" />Lead Sources</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Options that appear in the Lead Source dropdown when adding or editing a customer.</p>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-4">
          <div className="text-sm font-semibold text-zinc-900 mb-2">Add New Source</div>
          <div className="flex gap-2">
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. QR Truck, Instagram" className="h-11 flex-1" data-testid="new-source-input" onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }} />
            <button type="button" onClick={handleAdd} disabled={!newLabel.trim() || saving} className="px-4 py-2.5 bg-[#207024] hover:bg-[#185a1c] disabled:bg-zinc-300 text-white font-semibold rounded-lg flex items-center gap-1.5 text-sm" data-testid="add-source-btn"><Plus className="w-4 h-4" />Add</button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-100">
          {sources.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No sources yet — add one above.</div>}
          {sources.map((src) => (
            <div key={src.source_id} className="flex items-center gap-2 p-3" data-testid={`source-row-${src.source_id}`}>
              <input
                defaultValue={src.label}
                onBlur={(e) => handleRename(src, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                className={`flex-1 h-10 px-3 rounded-md border border-transparent hover:border-zinc-200 focus:border-[#207024] focus:outline-none text-sm ${!src.enabled ? "text-zinc-400 line-through" : "text-zinc-900"}`}
              />
              <button type="button" onClick={() => handleToggle(src)} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${src.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                {src.enabled ? "Active" : "Hidden"}
              </button>
              <button type="button" onClick={() => handleDelete(src)} className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Delete" data-testid={`delete-source-${src.source_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <p className="text-xs text-zinc-500 mt-3">Tap a label to rename. "Hidden" keeps historical customers tagged with that source but removes it from the dropdown for new entries.</p>
      </div>
      <BottomNav activeTab="settings" user={user} />
    </div>
  );
}
